import { getLoginToken } from "./login.core";

interface LogErrorContext {
  componentStack?: string;
  source?: string;
}

interface LogReqErrorInput {
  req: {
    input: RequestInfo | URL;
    init?: RequestInit;
  };
  res?: Response;
  resData: any | undefined;
}

const EXPECTED_REQ_ERROR_MESSAGES = new Set([
  "invalidToken",
  "tokenNotExists",
  "invalidUser",
  "missingShopId",
  "skuExists",
  "barcodeExists",
  "skuAndBarcodeExists",
]);

export function logBoundaryError(error: Error, info: { componentStack: string }) {
  logError(error, { componentStack: info.componentStack, source: "errorBoundary" });
}

export async function logError(error: unknown, context?: LogErrorContext) {
  await sendLog({
    app: "her-admin",
    message: buildErrorMessage(error, context),
    stack: getErrorStack(error, context?.componentStack),
    user: getLoginToken(),
  });
}

export async function logReqError(input: LogReqErrorInput) {
  const resData = getResData(input.resData);
  const message = getResDataMessage(resData);
  if (message && EXPECTED_REQ_ERROR_MESSAGES.has(message)) {
    return;
  }

  await sendLog({
    app: "her-admin",
    message: {
      page: getClientPage(),
      req: serializeReq(input.req),
      res: serializeResponse(input.res),
      resData,
      source: "fetch",
    },
    stack: getErrorStack(input.resData),
    user: getLoginToken(),
  });
}

async function sendLog(payload: { app: string; message: Record<string, unknown>; stack: string; user?: string }) {
  await fetch(process.env.NEXT_PUBLIC_LOG_URL + "/logError", {
    body: JSON.stringify(payload),
    headers: {
      "Content-Type": "application/json",
    },
    method: "POST",
  });
}

function buildErrorMessage(error: unknown, context?: LogErrorContext) {
  return {
    page: getClientPage(),
    componentStack: context?.componentStack,
    error: getResData(error),
    source: context?.source ?? "logError",
  };
}

function getErrorStack(error: unknown, componentStack?: string) {
  const errorStack = error instanceof Error && error.stack ? error.stack : Error().stack;

  if (!componentStack) {
    return errorStack || "";
  }

  return `${errorStack || ""}\n\nComponent stack:${componentStack}`;
}

function getResData(resData: unknown): Record<string, unknown> | undefined {
  if (resData === undefined) return undefined;

  if (resData instanceof Error) {
    return serializeError(resData);
  }

  if (typeof resData === "object" && resData !== null) {
    return resData as Record<string, unknown>;
  }

  return { value: resData };
}

function getResDataMessage(resData: Record<string, unknown> | undefined) {
  const message = resData?.message;
  return typeof message === "string" ? message : undefined;
}

function serializeError(error: Error): Record<string, unknown> {
  const serialized: Record<string, unknown> = {
    message: error.message,
    name: error.name,
    stack: error.stack,
  };

  if (error.cause instanceof Error) {
    serialized.cause = serializeError(error.cause);
  } else if (error.cause !== undefined) {
    serialized.cause = error.cause;
  }

  return serialized;
}

function serializeReq(req: LogReqErrorInput["req"]) {
  return {
    input: typeof req.input === "string" ? req.input : req.input.toString(),
    init: req.init
      ? {
          body: req.init.body,
          headers: req.init.headers,
          method: req.init.method,
        }
      : undefined,
  };
}

function serializeResponse(res?: Response) {
  if (!res) return undefined;

  return {
    status: res.status,
    statusText: res.statusText,
    url: res.url,
  };
}

function getClientPage(): string | undefined {
  if (typeof window === "undefined") {
    return undefined;
  }

  const { pathname, search } = window.location;
  return `${pathname}${search}` || undefined;
}
